import { inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { ConsultationStorageService } from '@services/consultation-storage.service';
import { NavigationService } from '@services/navigation.service';

@Injectable({
  providedIn: 'root',
})
export class SkipHasUpsellGuard implements CanActivate {
  private router = inject(Router);
  private consultationStorageService = inject(ConsultationStorageService);
  private navigationService = inject(NavigationService);

  /**
   * Prevents navigation to the current route if any upsell in the skip list has already been ordered or added,
   * redirecting the user to the next page in the consultation flow.
   */
  public async canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean | UrlTree> {
    const { upsellsFromOrder = [], upsellsFromConsultation = [] } = route.data['skipHasUpsell'] ?? {};

    return this.isUpsellPresentInOrderedTests(upsellsFromOrder) ||
      this.isUpsellPresentInConsultation(upsellsFromConsultation)
      ? this.getNextPageUrlTree(state.url)
      : true;
  }

  /**
   * Checks whether any upsell in the skip list was previously ordered through the order page and exists in the
   * customer ordered tests.
   */
  private isUpsellPresentInOrderedTests(upsells: string[]): boolean {
    return (
      this.consultationStorageService.orderedTests?.some((test) => upsells.includes(test?.customer_tests_name)) ?? false
    );
  }

  /**
   * Checks whether any upsell in the skip list was added during the active consultation request workflow and is
   * currently stored in upsells local storage.
   */
  private isUpsellPresentInConsultation(upsells: string[]): boolean {
    return this.consultationStorageService.upsells?.some((upsell) => upsells.includes(upsell)) ?? false;
  }

  /**
   * Resolves the next page URL for the current consultation and returns it as a UrlTree.
   */
  private async getNextPageUrlTree(currentUrl: string): Promise<UrlTree> {
    const nextPageUrl = await this.navigationService.getConsultationRequestNextPageUrl(
      this.consultationStorageService.treatmentType,
      currentUrl,
    );

    return this.router.createUrlTree([nextPageUrl]);
  }
}
